// Planner vibes. Every entry here must be something the planner can actually act
// on, and every description must promise only what our data holds — the numbers
// below are plannable places / cities covered (of 5,698) as measured 2026-09-06.
//
// 'Foodie' was REMOVED: the place database holds ZERO food-category rows and
// meals are deliberately scheduled as unbranded Lunch/Dinner time placeholders,
// so "street food, fine dining & markets" was a promise nothing could keep.
// Descriptions for romantic / wellness / wildlife / luxury were rewritten for the
// same reason — they advertised wine, dinners, five-star stays and VIP
// experiences, none of which exist in the data (we hold no restaurants or hotels).
export const VIBES = [
  // 63,465 places / 4,447 cities
  { id: 'relaxing',    label: 'Relaxing',    em: '🌴', c: '#4ecb8d', d: 'Parks, beaches & slow mornings' },
  // 9,995 / 2,850
  { id: 'adventurous', label: 'Adventurous', em: '🧗', c: '#f5a623', d: 'Trails, peaks & bold escapes' },
  // 13,520 / 3,261
  { id: 'romantic',    label: 'Romantic',    em: '💕', c: '#e91e63', d: 'Sunset viewpoints, gardens & old-town lanes' },
  // 276,613 / 5,414 — our deepest category by far
  { id: 'cultural',    label: 'Cultural',    em: '🏛️', c: '#9c27b0', d: 'Museums, history & local art' },
  // 1,264 / 793
  { id: 'wellness',    label: 'Wellness',    em: '🧘', c: '#00bcd4', d: 'Thermal baths, spas & quiet retreats' },
  // 2,407 / 1,475
  { id: 'wildlife',    label: 'Wildlife',    em: '🦁', c: '#43a047', d: 'Zoos, nature reserves & safari parks' },
  // 4,607 / 999 — low city count because beaches are coastal, not because data is missing
  { id: 'beach',       label: 'Beach',       em: '🏖️', c: '#03a9f4', d: 'Sun, sand & crystal waters' },
  // 1,469 / 760
  { id: 'luxury',      label: 'Luxury',      em: '💎', c: '#ffb300', d: 'Palaces, opera houses & grand landmarks' },
];


// Day-construction models the planner can use. Every description below is what
// the model DEMONSTRABLY does, measured on the quality harness over 16 cities x
// {3,5} days rather than described aspirationally:
//
//   model      must-see  worst leg  worst day  time-of-day
//   balanced      79%      6 km       9 km        53%
//   anchor        82%      8 km      12 km        71%
//   optimized     80%      9 km      12 km        60%
//
// 'balanced' stays the default: tightest days of the three without giving up
// must-see coverage. 'anchor' finds the most must-sees and times them best but
// travels furthest. 'optimized' fits the most in and was the only model with no
// flagged plan in the sweep.
//
// A fourth model ('ai', where the LLM builds the whole plan) exists in the
// backend and is deliberately NOT listed: it plans the tightest days of all
// (4 km worst leg) but finds the fewest must-sees (76%), so it is not offered
// until that gap closes.
// Order matters: the first entry is shown first, and 'anchor' is the DEFAULT in both
// AIAccordionScreen and routes/ai.js. Every claim below is a harness measurement over 32
// plans (16 cities x days 3 and 5) on the same candidate pool, not a description of intent:
//
//   model      must-see  maxLeg  worstDay  attractions/plan
//   anchor        86%      8km     12km        14.9
//   balanced      84%      6km      9km        15.4
//   optimized     83%      9km     12km        11.8
export const PLANNER_MODELS = [
  {
    id: 'anchor',
    label: 'Landmark-led',
    em: '🗿',
    short: 'Most must-sees',
    d: 'Gives every day one famous headliner and builds around it, so the big sights are spread across the trip instead of stacked on day one. Finds the most must-sees, and fits time-sensitive places into the right part of the day more often — at the cost of a little more travel. The default.',
  },
  {
    id: 'balanced',
    label: 'Balanced',
    em: '⚖️',
    short: 'Least travel',
    d: 'Groups each day into one walkable pocket of the city. The least travel of the three — about 2 km less on the longest hop — for two or three fewer must-sees. Choose it if a tight day matters more than the last big sight.',
  },
  {
    id: 'optimized',
    label: 'Time-budgeted',
    em: '🧭',
    short: 'Earliest finish',
    // The old copy read "See the most / Fullest days". That is measurably backwards: this
    // model plans the FEWEST attractions of the three (11.8 a trip against balanced's
    // 15.4), because it stops adding to a day once the real travel-and-visit budget is
    // spent. What it actually wins is finishing early — 18:30 latest end and no day past
    // 19:00, against 19:05 for the other two.
    d: 'Stops filling a day when the real travel and visiting time is spent, so days end earliest and never run late. Plans fewer places than the others, not more.',
  },
];